import { Coding } from '@medplum/fhirtypes'
import {
  CLINICAL_DOCUMENT_TYPES_AND_CODES,
  ClinicalDocumentType,
  NEEDS_FOR_DOCUMENT_TYPES_AND_CODES,
  NeedsForDocumentType,
  NOT_APPLICABLE_DOCUMENT_TYPE
} from 'utils/staticLists/documentTypes'
import { THERAPY_TYPES_AND_CODES, TherapyTypes } from 'utils/staticLists/therapies'
import { SectionDependentItems } from './types/questionnaire'
import { ModifierAFormField } from './sections/ModifierA/formField'
import { ModifierAQuestionnaireItem } from './sections/ModifierA/questionnaire'
import { ModifierBFormField } from './sections/ModifierB/formField'
import { ModifierBQuestionnaireItem } from './sections/ModifierB/questionnaire'
import { BradenScaleFormField } from './sections/BradenScale/formField'
import { BradenScaleQuestionnaireItem } from './sections/BradenScale/questionnaire'
import { CNASkilledTasksFormField } from './sections/CNASkilledTasks/formField'
import { CNASkilledTasksQuestionnaireItem } from './sections/CNASkilledTasks/questionnaire'

export enum PATQuestionnaireSections {
  AssessmentOverview = 'assessment-overview',
  PatientBackground = 'patient-background',
  ModifierA = 'modifier-a',
  BradenScale = 'braden-scale',
  ModifierB = 'modifier-b',
  CNASkilledTasks = 'cna-skilled-tasks',
  ScoreAndSummary = 'score-and-summary',
  NextSteps = 'next-steps'
}

export const PATAssessmentAnchors = [
  {
    key: PATQuestionnaireSections.AssessmentOverview,
    href: `#${PATQuestionnaireSections.AssessmentOverview}`,
    title: 'Assessment Overview'
  },
  {
    key: PATQuestionnaireSections.PatientBackground,
    href: `#${PATQuestionnaireSections.PatientBackground}`,
    title: 'Patient Background'
  },
  {
    key: PATQuestionnaireSections.ModifierA,
    href: `#${PATQuestionnaireSections.ModifierA}`,
    title: 'Modifier A'
  },
  {
    key: PATQuestionnaireSections.BradenScale,
    href: `#${PATQuestionnaireSections.BradenScale}`,
    title: 'Braden Scale'
  },
  {
    key: PATQuestionnaireSections.ModifierB,
    href: `#${PATQuestionnaireSections.ModifierB}`,
    title: 'Modifier B'
  },
  {
    key: PATQuestionnaireSections.CNASkilledTasks,
    href: `#${PATQuestionnaireSections.CNASkilledTasks}`,
    title: 'CNA Skilled Tasks'
  },
  {
    key: PATQuestionnaireSections.ScoreAndSummary,
    href: `#${PATQuestionnaireSections.ScoreAndSummary}`,
    title: 'Score and Summary'
  },
  {
    key: PATQuestionnaireSections.NextSteps,
    href: `#${PATQuestionnaireSections.NextSteps}`,
    title: 'Next Steps'
  }
]

export const PATScoreMapping = {
  // Maps the score to the corresponding duration (in minutes)
  [PATQuestionnaireSections.ModifierA]: [
    [0, 0],
    [1, 30],
    [5, 60],
    [9, 90]
  ],
  // Maps the score of the results of Braden Scale to the definition of Braden Scale
  [PATQuestionnaireSections.BradenScale]: [
    [0, 9, 4],
    [10, 12, 3],
    [13, 14, 2],
    [15, 18, 1],
    [19, 23, 0]
  ],
  // Maps the score to the corresponding duration (in minutes)
  [PATQuestionnaireSections.ModifierB]: [
    [0, 0],
    [1, 30],
    [9, 60]
  ],
  // Maps the score to the corresponding duration (in hours)
  [PATQuestionnaireSections.CNASkilledTasks]: [
    [0, 0],
    [5, 1],
    [12, 2],
    [19, 3],
    [26, 4],
    [36, 5],
    [46, 6],
    [56, 7],
    [66, 8]
  ]
}

const clinicalDocumentTypes = {
  ...CLINICAL_DOCUMENT_TYPES_AND_CODES,
  ...NOT_APPLICABLE_DOCUMENT_TYPE
}
const needsForDocumentTypes = {
  ...NEEDS_FOR_DOCUMENT_TYPES_AND_CODES,
  ...NOT_APPLICABLE_DOCUMENT_TYPE
}
export type StaticListType =
  | Record<NeedsForDocumentType, { label: string; code: string }>
  | Record<ClinicalDocumentType, { label: string; code: string }>
  | Record<TherapyTypes, { label: string; code: string }>

export const formatStaticLists = (staticList: StaticListType): { valueCoding: Coding }[] => {
  return Object.keys(staticList).map((docType) => ({
    valueCoding: {
      display: staticList[docType].label,
      code: staticList[docType].code
    }
  }))
}

export const PAT_STATIC_DATA = {
  clinicalDocumentTypes: formatStaticLists(clinicalDocumentTypes),
  needsForDocumentTypes: formatStaticLists(needsForDocumentTypes),
  therapyTypes: formatStaticLists(THERAPY_TYPES_AND_CODES)
}

export const ASSESSMENTS_STATIC_DATA = {
  pat: PAT_STATIC_DATA
}

export const skilledTaskTitleMap = {
  'cna-skilled-tasks/bathing/question': 'Bathing',
  'cna-skilled-tasks/braces-splints-stockings/question':
    'Braces, Splints, and/or Pressure Stockings',
  'cna-skilled-tasks/dressing/question': 'Dressing',
  'cna-skilled-tasks/eating/question': 'Eating or Oral Feedings',
  'cna-skilled-tasks/tube-feedings/question': 'Enteral G-tube/J-tube feedings',
  'cna-skilled-tasks/medication/question': 'Medication',
  'cna-skilled-tasks/mobility/question': 'Mobility/Ambulation',
  'cna-skilled-tasks/meal-prep/question': 'Modified meal preparation',
  'cna-skilled-tasks/grooming/question': 'Grooming',
  'cna-skilled-tasks/skin-care-application/question': 'Skincare',
  'cna-skilled-tasks/skin-care/question': 'Skincare (OTC)',
  'cna-skilled-tasks/rom-exercise/question': 'ROM/Exercise',
  'cna-skilled-tasks/respiratory-care/question': 'Chest Percussive Therapy',
  'cna-skilled-tasks/oxygen-therapy/question': 'Oxygen Therapy',
  'cna-skilled-tasks/oral-suctioning/question': 'Member requires oral suctioning',
  'cna-skilled-tasks/bowel-frequency/question': 'Bowel Frequency',
  'cna-skilled-tasks/bowel-hygiene/question': 'Bowel Hygiene',
  'cna-skilled-tasks/bowel-elimination/question': 'Bowel Elimination - Ostomy',
  'cna-skilled-tasks/bowel-program/question': 'Bowel Program',
  'cna-skilled-tasks/catheter-care/question': 'Catheter Care',
  'cna-skilled-tasks/menses/question': 'Menses',
  'cna-skilled-tasks/urinary-hygiene/question': 'Urinary Hygiene',
  'cna-skilled-tasks/positioning/question': 'Positioning',
  'cna-skilled-tasks/transfers/question': 'Transfers',
  'cna-skilled-tasks/vitals-physician/question': 'Vital signs, physician ordered',
  'cna-skilled-tasks/vitals-nurse/question': 'Vital signs, nurse ordered'
}

export const UNANSWERED_DECIMAL_DEFAULT = -1

export const UNANSWERED_SUMMARY_INPUT_DEFAULT = 'Pending Assessment Completion'

export const SALESFORCE_QUALIFICATION_VALUES_TO_UPDATE = {
  patient: {
    birth_date: { key: 'patient-background/patient-verification/dob' },
    name: { key: 'patient-background/patient-verification/legal-name' }
  },
  care_hours: { key: 'score-and-summary/total' },
  pat_status: { key: 'next-steps/qualification-status' },
  docs_needed: { key: 'next-steps/requested-docs', as: [] },
  needs_for_docs: { key: 'next-steps/specific-needs-for-docs', as: [] }
}

export interface SFValuesToUpdateType {
  patient: {
    birth_date: string
    name: string
  }
  care_hours: number
  pat_status: string
  docs_needed?: string[]
  needs_for_docs?: string[]
}

export const SECTION_DEPENDENT_ANSWER_LIST_FILTERS = {
  [SectionDependentItems.BRADEN_SCALE]: [PATQuestionnaireSections.BradenScale, 'question'],
  [SectionDependentItems.MODIFIER_A]: [PATQuestionnaireSections.ModifierA, 'question'],
  [SectionDependentItems.MODIFIER_B]: [PATQuestionnaireSections.ModifierB, 'question'],
  [SectionDependentItems.CNA_SKILLED_TASKS]: [PATQuestionnaireSections.CNASkilledTasks, 'question'],
  [SectionDependentItems.SKILLED_NEEDS_LIST]: [
    PATQuestionnaireSections.CNASkilledTasks,
    'question'
  ],
  [SectionDependentItems.DIAGNOSES_LIST]: [['primary-diagnoses'], ['other-diagnoses']],
  [SectionDependentItems.TOTAL_ASSESSMENT]: [PATQuestionnaireSections.ScoreAndSummary],
  [SectionDependentItems.QUALIFICATION_STATUS]: [PATQuestionnaireSections.ScoreAndSummary, 'total']
}

export const SECTION_DEPENDENT_HELP_TEXT = {
  [SectionDependentItems.BRADEN_SCALE]: 'Braden score not obtained, section incomplete'
}

// Shared Modifier A question fields structure
const MODIFIER_A_QUESTION_FIELDS = [
  {
    formField: ModifierAFormField.MODIFIER_A_1_QUESTION,
    linkId: ModifierAQuestionnaireItem.MODIFIER_A_1_QUESTION
  },
  {
    formField: ModifierAFormField.MODIFIER_A_2_QUESTION,
    linkId: ModifierAQuestionnaireItem.MODIFIER_A_2_QUESTION
  },
  {
    formField: ModifierAFormField.MODIFIER_A_3_QUESTION,
    linkId: ModifierAQuestionnaireItem.MODIFIER_A_3_QUESTION
  },
  {
    formField: ModifierAFormField.MODIFIER_A_4_QUESTION,
    linkId: ModifierAQuestionnaireItem.MODIFIER_A_4_QUESTION
  }
] as const

// Shared Modifier B question fields structure
const MODIFIER_B_QUESTION_FIELDS = [
  {
    formField: ModifierBFormField.MODIFIER_B_1_QUESTION,
    linkId: ModifierBQuestionnaireItem.MODIFIER_B_1_QUESTION
  },
  {
    formField: ModifierBFormField.MODIFIER_B_2_QUESTION,
    linkId: ModifierBQuestionnaireItem.MODIFIER_B_2_QUESTION
  },
  {
    formField: ModifierBFormField.MODIFIER_B_3_QUESTION,
    linkId: ModifierBQuestionnaireItem.MODIFIER_B_3_QUESTION
  },
  {
    formField: ModifierBFormField.MODIFIER_B_4_QUESTION,
    linkId: ModifierBQuestionnaireItem.MODIFIER_B_4_QUESTION
  },
  {
    formField: ModifierBFormField.MODIFIER_B_5_QUESTION,
    linkId: ModifierBQuestionnaireItem.MODIFIER_B_5_QUESTION
  },
  {
    formField: ModifierBFormField.MODIFIER_B_6_QUESTION,
    linkId: ModifierBQuestionnaireItem.MODIFIER_B_6_QUESTION
  },
  {
    formField: ModifierBFormField.MODIFIER_B_7_QUESTION,
    linkId: ModifierBQuestionnaireItem.MODIFIER_B_7_QUESTION
  }
] as const

const BRADEN_SCALE_QUESTION_FIELDS = [
  {
    formField: BradenScaleFormField.BRADEN_SCALE_SENSORY_QUESTION,
    linkId: BradenScaleQuestionnaireItem.BRADEN_SCALE_SENSORY_QUESTION
  },
  {
    formField: BradenScaleFormField.BRADEN_SCALE_MOISTURE_QUESTION,
    linkId: BradenScaleQuestionnaireItem.BRADEN_SCALE_MOISTURE_QUESTION
  },
  {
    formField: BradenScaleFormField.BRADEN_SCALE_ACTIVITY_QUESTION,
    linkId: BradenScaleQuestionnaireItem.BRADEN_SCALE_ACTIVITY_QUESTION
  },
  {
    formField: BradenScaleFormField.BRADEN_SCALE_MOBILITY_QUESTION,
    linkId: BradenScaleQuestionnaireItem.BRADEN_SCALE_MOBILITY_QUESTION
  },
  {
    formField: BradenScaleFormField.BRADEN_SCALE_NUTRITION_QUESTION,
    linkId: BradenScaleQuestionnaireItem.BRADEN_SCALE_NUTRITION_QUESTION
  },
  {
    formField: BradenScaleFormField.BRADEN_SCALE_FRICTION_AND_SHEAR_QUESTION,
    linkId: BradenScaleQuestionnaireItem.BRADEN_SCALE_FRICTION_AND_SHEAR_QUESTION
  }
] as const

// Shared CNA Skilled Tasks question fields structure
const CNA_SKILLED_TASKS_QUESTION_FIELDS = [
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_BATHING_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BATHING_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_DRESSING_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_DRESSING_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_EATING_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_EATING_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_TUBE_FEEDINGS_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_TUBE_FEEDINGS_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEDICATION_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_MEDICATION_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_MOBILITY_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_MOBILITY_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEAL_PREP_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_MEAL_PREP_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_GROOMING_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_GROOMING_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_ROM_EXERCISE_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_ROM_EXERCISE_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_RESPIRATORY_CARE_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_RESPIRATORY_CARE_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_OXYGEN_THERAPY_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_OXYGEN_THERAPY_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_ORAL_SUCTIONING_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_ORAL_SUCTIONING_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_HYGIENE_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_HYGIENE_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_PROGRAM_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_PROGRAM_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_CATHETER_CARE_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_CATHETER_CARE_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_MENSES_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_MENSES_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_URINARY_HYGIENE_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_URINARY_HYGIENE_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_POSITIONING_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_POSITIONING_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_TRANSFERS_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_TRANSFERS_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_QUESTION
  },
  {
    formField: CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_NURSE_QUESTION,
    linkId: CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_NURSE_QUESTION
  }
] as const

// Mapping of section IDs to their question fields arrays.
export const SECTION_QUESTION_FIELDS_MAP: Record<
  string,
  ReadonlyArray<{ formField: string; linkId: string }>
> = {
  [PATQuestionnaireSections.ModifierA]: MODIFIER_A_QUESTION_FIELDS,
  [PATQuestionnaireSections.BradenScale]: BRADEN_SCALE_QUESTION_FIELDS,
  [PATQuestionnaireSections.ModifierB]: MODIFIER_B_QUESTION_FIELDS,
  [PATQuestionnaireSections.CNASkilledTasks]: CNA_SKILLED_TASKS_QUESTION_FIELDS
}

// Add sections here as they are migrated from controlled to uncontrolled
export const UNCONTROLLED_FORM_SECTION: Set<PATQuestionnaireSections> = new Set([
  PATQuestionnaireSections.AssessmentOverview,
  PATQuestionnaireSections.PatientBackground,
  PATQuestionnaireSections.BradenScale,
  PATQuestionnaireSections.ModifierA,
  PATQuestionnaireSections.ModifierB,
  PATQuestionnaireSections.NextSteps,
  PATQuestionnaireSections.CNASkilledTasks,
  PATQuestionnaireSections.ScoreAndSummary
])
